import { useMutation, useQuery, useQueryClient } from "react-query";
import { axiosInstance } from "@src/api/axiosConfig";
import { axiosPath } from "@src/api/axiosPath";
import {
    CommunityListFilterType,
    CommunityListResponse,
    CommunityPostDetailDto,
} from "./types";

export const initCommunityListFilters: CommunityListFilterType = {
    page: 1,
    limit: 10,
};

// 커뮤니티 글 목록 조회
const fetchCommunityList = async (
    params: CommunityListFilterType,
): Promise<CommunityListResponse> => {
    const response = await axiosInstance.get(axiosPath.community, { params });

    return response.data;
};

export const useCommunityList = (params: CommunityListFilterType) => {
    return useQuery<CommunityListResponse, Error>(
        ["communityList", params],
        () => fetchCommunityList(params),
        { keepPreviousData: true },
    );
};

// 커뮤니티 글 상세 조회
const fetchCommunityDetail = async (
    postNo: number,
): Promise<CommunityPostDetailDto> => {
    const response = await axiosInstance.get(`${axiosPath.community}/${postNo}`);

    return response.data;
};

export const useCommunityDetail = (postNo: number | undefined) => {
    return useQuery<CommunityPostDetailDto, Error>(
        ["communityDetail", postNo],
        () => fetchCommunityDetail(Number(postNo)),
        { enabled: Boolean(postNo) },
    );
};

// 커뮤니티 글 삭제
const deleteCommunityPost = async (postNo: number): Promise<void> => {
    await axiosInstance.delete(`${axiosPath.community}/${postNo}`);
};

export const useDeleteCommunityPost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (postNo: number) => deleteCommunityPost(postNo),
        onSuccess: () => {
            queryClient.invalidateQueries("communityList", {
                refetchInactive: true,
            });
        },
    });
};

// 커뮤니티 댓글 삭제
const deleteCommunityComment = async (commentNo: number): Promise<void> => {
    await axiosInstance.delete(`${axiosPath.community}/comments/${commentNo}`);
};

export const useDeleteCommunityComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (commentNo: number) => deleteCommunityComment(commentNo),
        onSuccess: () => {
            queryClient.invalidateQueries("communityDetail", {
                refetchInactive: true,
            });
        },
    });
};
